import React from "react";
import { Link } from "react-router-dom";
import SmartImage from "./SmartImage";
import CallToActionWithModalV2 from "./CallToActionWithModalV2";
import IconBulletList from "./IconBulletList";
import IconBulletListV2 from "./IconBulletListV2";
import OfferCardDeck from "./OfferCardDeck";
import { Button } from "./ui";
import { Headline, Paragraph } from "./ui/Typography";

const SplitScreenContent = ({
  headline,
  intro,
  blocks = [],
  className = "",
  padding = "pt-24 py-4 px-6 lg:px-24",
}) => {
  const renderBlock = (block, index) => {
    switch (block.type) {
      case "image":
        return (
          <div key={index} className={block.wrapperClass || "mb-8"}>
            <SmartImage
              src={block.src}
              alt={block.alt}
              width={block.width || 1200}
              height={block.height || 800}
              highQuality={block.highQuality || true}
              className={
                block.className || "w-full h-128 object-cover rounded-sm mb-20"
              }
            />
          </div>
        );

      case "heading":
        return (
          <Headline
            key={index}
            variant="splitscreen-heading"
            color="grayDark"
            animate={false}
            className={block.className || ""}
          >
            {block.text}
          </Headline>
        );

      case "paragraph":
        return (
          <Paragraph
            key={index}
            variant="splitscreen-content"
            color="gray"
            className={block.className || ""}
            style={block.style || {}}
            dangerouslySetInnerHTML={
              typeof block.text === "string"
                ? { __html: block.text }
                : undefined
            }
          >
            {typeof block.text === "string" ? undefined : block.text}
          </Paragraph>
        );

      case "component":
        if (block.component === "CallToActionWithModalV2") {
          return (
            <div key={index} className={block.wrapperClass || "mt-12"}>
              <CallToActionWithModalV2 {...block.props} />
            </div>
          );
        }
        if (block.component === "IconBulletList") {
          return (
            <div key={index} className={block.wrapperClass || "mt-8"}>
              <IconBulletList {...block.props} />
            </div>
          );
        }
        if (block.component === "IconBulletListV2") {
          return (
            <div key={index} className={block.wrapperClass || "mt-8"}>
              <IconBulletListV2 {...block.props} />
            </div>
          );
        }
        if (block.component === "OfferCardDeck") {
          return (
            <div key={index} className={block.wrapperClass || "mb-8"}>
              <OfferCardDeck {...block.props} />
            </div>
          );
        }
        return null;

      case "spacer":
        return <div key={index} className={block.className || "my-24"} />;

      case "buttons":
        return (
          <div
            key={index}
            className={
              block.wrapperClass || "flex flex-col sm:flex-row gap-4 mt-8"
            }
          >
            {block.buttons.map((button, buttonIndex) => (
              <Button
                key={buttonIndex}
                as={button.to ? Link : "a"}
                to={button.to}
                href={button.href}
                target={button.target}
                rel={button.rel}
                variant={
                  button.variant ||
                  (buttonIndex === 0 ? "secondary" : "primary")
                }
                size={button.size || "medium"}
                className={button.className || ""}
              >
                {button.text}
              </Button>
            ))}
          </div>
        );

      case "list":
        const ListTag = block.ordered ? "ol" : "ul";
        return (
          <ListTag
            key={index}
            className={
              block.className ||
              "list-disc list-inside mb-16 space-y-2 text-gray-700 mt-[-40px]"
            }
            style={block.style || {}}
          >
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{item}</li>
            ))}
          </ListTag>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`${padding} ${className}`}>
      <div className="prose prose-lg max-w-4xl">
        {/* Main Headline */}
        {headline && (
          <Headline variant="splitscreen" color="grayDark" animate={false}>
            {headline}
          </Headline>
        )}

        {/* Intro Paragraph */}
        {intro && (
          <Paragraph
            variant="splitscreen-intro"
            color="gray"
            dangerouslySetInnerHTML={
              typeof intro === "string" ? { __html: intro } : undefined
            }
          >
            {typeof intro === "string" ? undefined : intro}
          </Paragraph>
        )}

        {/* Content Blocks */}
        {blocks.map((block, index) => renderBlock(block, index))}
      </div>
    </div>
  );
};

export default SplitScreenContent;
